import React, { useState, useEffect } from "react";
import axios from "../common/axios";
import {
  Link as RouterLink,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import Swal from "sweetalert2";
import { EyeOff } from "lucide-react";
import { Button } from "src/components/ui/button";
import { Input } from "src/components/ui/input";
import { Label } from "src/components/ui/label";
import { Checkbox } from "src/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "src/components/ui/dialog";

export default function SignUp() {
  const navigate = useNavigate(); // useNavigate 사용
  const [searchParams] = useSearchParams();
  const [username, setUsername] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [email, setEmail] = useState(searchParams.get("email") || "");
  const [passwordValid, setPasswordValid] = useState([
    false,
    false,
    false,
    false,
  ]);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [openTermsDialog, setOpenTermsDialog] = useState(false);
  const [isEmailValid, setIsEmailValid] = useState(true);
  const [strength, setStrength] = useState(0);

  function getPasswordStrength(password) {
    let score = 0;
    const consecutivePattern = /(.)\1\1+/; // 3번 이상 연속되는 문자

    if (password.length >= 10) score++;
    if (/[^a-zA-Z0-9]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[A-Z]/.test(password)) score++;
    if (consecutivePattern.test(password)) score = 0; // 연속된 문자가 있다면 점수를 0으로 초기화

    return score;
  }

  useEffect(() => {
    setStrength(getPasswordStrength(password));
  }, [password]);

  const handleEmailChange = (e) => {
    const emailInput = e.target.value;
    setEmail(emailInput);
    setIsEmailValid(validateEmail(emailInput) || emailInput === "");
  };

  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
    return emailRegex.test(email);
  };

  const isAllowedDomain = (email) => {
    const lower = email.toLowerCase();
    return (
      lower.endsWith("@samsung.com") || lower.endsWith("@partners.samsung.com")
    );
  };

  const handleOpenTermsDialog = () => setOpenTermsDialog(true);
  const handleCloseTermsDialog = () => setOpenTermsDialog(false);

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    const validity = [
      value.length >= 8,
      /[A-Z]/.test(value),
      /[a-z]/.test(value),
      /[!@#$%^&*]/.test(value),
    ];
    setPasswordValid(validity);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // API call logic here
    try {
      if (!isAllowedDomain(email)) {
        Swal.fire(
          "Error",
          "@samsung.com 또는 @partners.samsung.com 이메일만 회원가입 가능합니다.",
          "error",
        );
        return;
      }

      const response = await axios.post("/loa_signup", {
        username,
        name,
        password,
        email,
        company: "삼성중공업",
      });

      if (response.data.success) {
        Swal.fire(
          "성공",
          "회원 가입이 완료되었습니다. 가입 승인 메일을 받으신 후에 로그인이 가능합니다.",
          "success",
        );
        navigate("/login"); // Redirect on success
      } else {
        // Handle backend validation errors
        Swal.fire("Error", response.data.message || "회원 가입 실패", "error");
      }
    } catch (error) {
      // Handle network or other unexpected errors
      Swal.fire(
        "Error",
        error.response?.data?.message || "회원 가입에 문제가 발생했습니다.",
        "error",
      );
    }
  };

  // State to manage if the form is valid to submit
  const [isFormValid, setIsFormValid] = useState(false);

  // Check if all fields are valid and if passwords match
  useEffect(() => {
    const formIsValid =
      username &&
      password &&
      confirmPassword &&
      email &&
      isEmailValid &&
      isAllowedDomain(email) &&
      password === confirmPassword &&
      passwordValid.every(Boolean) &&
      acceptedTerms;

    setIsFormValid(formIsValid);
  }, [
    username,
    password,
    confirmPassword,
    email,
    passwordValid,
    acceptedTerms,
    isEmailValid,
  ]);

  return (
    <>
      <Dialog
        open={openTermsDialog}
        onOpenChange={(open) => !open && handleCloseTermsDialog()}
      >
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>개인정보 수집 이용 동의</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-muted-foreground">
            <p>
              결재 포탈 시스템 이용을 위하여 본인의 개인정보를 수집·이용하고자
              하는 경우에는「개인정보 보호법」 제15조 제1항 제1호, 제22조
              제3항에 따라 본인의 동의를 얻어야 합니다. 이에 본인은 귀 사가
              아래의 내용과 같이 본인의 개인정보를 수집·이용 또는 제공하는 것에
              동의합니다.
            </p>

            <h3>[수집·이용에 관한 사항]</h3>
            <ul>
              <li>
                <h4>1. 수집·이용 목적</h4>
                <p>결재 포탈 시스템 이용</p>
              </li>
              <li>
                <h4>2. 수집·이용할 항목</h4>
                <p>이메일,이름,회사명, 사업자등록번호</p>
              </li>
              <li>
                <h4>3. 보유·이용 기간</h4>
                <p>
                  위 개인정보는 수집·이용에 관한 동의일로부터 동의 철회시까지
                  보유·이용할 수 있습니다. 단, 동의 철회일 후에는 위에 기재된
                  목적과 관련된 분쟁 해결, 민원처리, 법령상 의무이행을 위하여
                  필요한 범위 내에서만 보유·이용됩니다.
                </p>
              </li>
              <li>
                <h4>4. 동의를 거부할 권리 및 동의를 거부할 경우의 불이익</h4>
                <p>
                  위 개인정보의 수집·이용에 대한 동의를 거부할 수 있으며, 동의
                  후에도 언제든지 철회 가능합니다. 다만, 동의하지 않은 수집·이용
                  목적과 관련된 광고심의시스템 이용 관련 서비스 및 안내 등의
                  편의는 제한될 수 있습니다.
                </p>
              </li>
            </ul>
          </div>
          <label className="flex items-center gap-2">
            <Checkbox
              checked={acceptedTerms}
              onCheckedChange={(checked) => setAcceptedTerms(checked === true)}
            />
            위 내용에 동의합니다.
          </label>
          <DialogFooter>
            <Button onClick={handleCloseTermsDialog}>닫기</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="flex min-h-screen flex-col items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-7">
            <div className="flex items-center justify-center mb-4">
              <a href="http://www.samsungshi.com/kor/default.aspx">
                <img
                  alt="Logo"
                  src="/images/g1313.png"
                  className="h-8 w-auto"
                />
              </a>
            </div>
            <h1 className="text-foreground font-extrabold text-[19px] tracking-tight mb-2">
              삼성중공업 현장공무 LOA 결재 시스템
            </h1>
            <p className="text-muted-foreground text-sm">
              계정을 새로 등록하세요
            </p>
          </div>

          <div className="rounded-lg border bg-card p-6">
            <h2 className="text-foreground font-bold text-base mb-1">
              회원 가입
            </h2>
            <p className="text-muted-foreground text-xs mb-5">
              아래 정보를 입력해 가입을 요청하세요.
            </p>

            <form onSubmit={handleSubmit}>
              <div className="mb-3.5">
                <Label htmlFor="username" className="mb-1.5 block">
                  로그인 아이디
                </Label>
                <Input
                  id="username"
                  type="text"
                  name="username"
                  placeholder="로그인 아이디"
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="off"
                />
              </div>
              <div className="mb-3.5">
                <Label htmlFor="name" className="mb-1.5 block">
                  사용자 이름
                </Label>
                <Input
                  id="name"
                  type="text"
                  name="name"
                  placeholder="사용자 이름"
                  onChange={(e) => setName(e.target.value)}
                  autoComplete="off"
                />
              </div>
              <div className="mb-3.5">
                <Label htmlFor="password" className="mb-1.5 block">
                  비밀번호
                </Label>
                <div className="relative mb-2">
                  <Input
                    id="password"
                    type="password"
                    name="password"
                    placeholder="비밀번호"
                    value={password}
                    onChange={handlePasswordChange}
                    autoComplete="off"
                  />
                  <span className="absolute top-1/2 right-3 -translate-y-1/2 text-muted-foreground">
                    <EyeOff size={18} />
                  </span>
                </div>
                <div className="flex items-center mb-2">
                  <div
                    className={`flex-1 ${
                      strength > 0 ? "bg-green-500" : "bg-muted"
                    } rounded h-[5px] mr-2`}
                  ></div>
                  <div
                    className={`flex-1 ${
                      strength > 1 ? "bg-green-500" : "bg-muted"
                    } rounded h-[5px] mr-2`}
                  ></div>
                  <div
                    className={`flex-1 ${
                      strength > 2 ? "bg-green-500" : "bg-muted"
                    } rounded h-[5px] mr-2`}
                  ></div>
                  <div
                    className={`flex-1 ${
                      strength > 3 ? "bg-green-500" : "bg-muted"
                    } rounded h-[5px] mr-2`}
                  ></div>
                  <div
                    className={`flex-1 ${
                      strength > 4 ? "bg-green-500" : "bg-muted"
                    } rounded h-[5px]`}
                  ></div>
                </div>
                <p className="text-muted-foreground text-xs">
                  10자 이상의 대소문자, 특수문자를 포함해주세요. 3자리 이상의
                  연속된 문자는 쓸 수 없습니다.
                </p>
              </div>

              <div className="mb-3.5">
                <Label htmlFor="confirm-password" className="mb-1.5 block">
                  비밀번호 확인
                </Label>
                <Input
                  id="confirm-password"
                  placeholder="비밀번호 확인"
                  name="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="off"
                />
                {password &&
                  confirmPassword &&
                  password !== confirmPassword && (
                    <p className="text-destructive text-xs mt-1">
                      비밀번호가 일치하지 않습니다.
                    </p>
                  )}
              </div>
              <div className="mb-3.5">
                <Label htmlFor="email" className="mb-1.5 block">
                  이메일
                </Label>
                <Input
                  id="email"
                  placeholder="이메일"
                  name="email"
                  type="email"
                  value={email}
                  onChange={handleEmailChange}
                  autoComplete="off"
                />
                {!isEmailValid && email.length > 0 && (
                  <p className="text-destructive text-xs mt-1">
                    유효한 이메일 형식이 아닙니다.
                  </p>
                )}
                {isEmailValid &&
                  email.length > 0 &&
                  !isAllowedDomain(email) && (
                    <p className="text-destructive text-xs mt-1">
                      @samsung.com 또는 @partners.samsung.com 이메일만 회원가입
                      가능합니다.
                    </p>
                  )}
                <p className="text-muted-foreground text-xs mt-1">
                  @samsung.com 또는 @partners.samsung.com 이메일만 가입
                  가능합니다.
                </p>
              </div>

              <div className="mb-3.5">
                <Label htmlFor="company" className="mb-1.5 block">
                  회사명
                </Label>
                <Input
                  id="company"
                  name="company"
                  type="text"
                  value="삼성중공업"
                  disabled
                />
              </div>

              <div className="mb-4">
                <label className="flex items-center gap-2">
                  <Checkbox
                    name="toc"
                    value="1"
                    checked={acceptedTerms}
                    onCheckedChange={(checked) =>
                      setAcceptedTerms(checked === true)
                    }
                  />

                  <span className="font-semibold text-foreground text-sm">
                    개인정보 수집에{" "}
                    <a
                      href="#"
                      className="text-primary hover:underline"
                      onClick={handleOpenTermsDialog}
                    >
                      동의
                    </a>
                    합니다.
                  </span>
                </label>
              </div>
              <Button type="submit" disabled={!isFormValid} className="w-full">
                회원 가입 요청
              </Button>
            </form>
          </div>

          <p className="text-muted-foreground text-center text-xs mt-4">
            이미 삼성중공업 하이테크 결재 포탈의 회원인가요?{" "}
            <RouterLink
              className="text-primary font-semibold hover:underline"
              to={{ pathname: "/login" }}
            >
              로그인
            </RouterLink>
          </p>
        </div>
      </div>
    </>
  );
}
